/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { MainNavTabs } from '@components/molecules/tab/MainNavTabs'
import { AppBar as MuiAppBar, styled, AppBarProps as MuiAppBarProps } from '@mui/material'
import { grey, blue } from '@mui/material/colors'
import { motion } from 'framer-motion'

import { useAppSelector, ConfigStateType } from '@redux/index'

interface AppBarPropType extends MuiAppBarProps {
	open: boolean
}

interface PropsType {
	style?: React.CSSProperties
}

export const MainNav = (props: PropsType): JSX.Element => {
	// redux state
	const mode: ConfigStateType['mode'] = useAppSelector(state => state.config.mode)
	const isAdminDrawerHeadAnimated: boolean = useAppSelector(
		state => state.config.isAdminDrawerHeadAnimated,
	)
	const reduxBackgroundColor: string = useAppSelector(
		state => state.config.backgroundColor,
	)
	const drawerWidth: number = useAppSelector(state => state.config.drawerWidth)
	const closedDrawerWidth: number = useAppSelector(
		state => state.config.closedDrawerWidth,
	)
	const isOpenDrawer: boolean = useAppSelector(state => state.config.isOpenDrawer)

	const backgroundColor: string = React.useMemo(() => {
		return mode === 'admin' ? blue[700] : reduxBackgroundColor
	}, [mode])

	const AppBar = styled(MuiAppBar, {
		shouldForwardProp: prop => prop !== 'open',
	})<AppBarPropType>(({ theme, open }) => ({
		zIndex: theme.zIndex.drawer + 1,
		marginLeft: `${closedDrawerWidth}px`,
		width: `calc(100% - ${closedDrawerWidth}px)`,
		transition: theme.transitions.create(['width', 'margin'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		...(open && {
			marginLeft: `${drawerWidth}px`,
			width: `calc(100% - ${drawerWidth}px)`,
			transition: theme.transitions.create(['width', 'margin'], {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.enteringScreen,
			}),
		}),
	}))

	return (
		<AppBar
			position="fixed"
			color="transparent"
			css={[
				style,
				{
					backgroundColor: backgroundColor,
				},
				!isOpenDrawer ? { paddingLeft: 0 } : null,
				{ ...props.style },
			]}
			elevation={0}
			open={isOpenDrawer}
		>
			<View className="navOuter">
				<motion.div
					initial={
						isAdminDrawerHeadAnimated ? { opacity: 0, y: '-100%', height: 0 } : false
					}
					animate={{ opacity: 1, y: 0, height: '2.75rem' }}
					exit={{ opacity: 0, height: 0 }}
					transition={{ duration: mode === 'admin' ? 0.5 : 0, type: 'spring' }}
				>
					<View className="navInner">
						<MainNavTabs />
					</View>
				</motion.div>
			</View>
		</AppBar>
	)
}

const style = css({
	position: 'fixed',
	zIndex: 5,
	height: '2.8rem',
	borderBottom: `1px solid ${grey[300]}`,

	'.navOuter': {
		display: 'flex',
		justifyContent: 'flex-start',
		alignItems: 'center',

		'.navInner': {
			display: 'flex',
			justifyContent: 'flex-start',
		},
	},
})
